import React from 'react'
import { CButton, CCol, CRow } from '@coreui/react-pro'
import { dateUSFormat, twelveHourTimeWithZone } from '../../../utils/dateTimeUtils'
import { getPCCResultFile } from '../../../helper/pccApiHelper'
import FEATURES from '../../../utils/featuresConstants'
import { getFusion } from '../../../apiHelper/fusionApi'

export function PatientInsuranceDisplay({ insuranceInfoData, DisplayFunction }) {
  return (
    <>
      {insuranceInfoData?.coverages?.payers?.map((info, index) => (
        <div key={index}>
          <h5 className="mt-3">{info.payerRank || '-'} Insurance</h5>
          <CRow>
            <CCol>
              <DisplayFunction title={'Insurance Name'} value={info?.payerName || '-'} />
            </CCol>
            <CCol>
              <DisplayFunction
                title={'Issuer'}
                value={
                  <div className={'d-flex flex-column'}>
                    <span>{info.issuer?.issuerName}</span>
                    <span>{info?.issuer?.issuerAddress?.addressLine1 || '-'}</span>
                    <span>{info?.issuer?.issuerAddress?.addressLine2 || ''}</span>
                    <span>
                      {info?.issuer?.issuerAddress?.issuerAddress?.city || ''}{' '}
                      {info?.issuer?.issuerAddress?.issuerAddress?.state || ''}{' '}
                      {info?.issuer?.issuerAddress?.issuerAddress?.postalCode || ''}
                    </span>
                  </div>
                }
              />
            </CCol>
          </CRow>
          <CRow>
            <CCol>
              <DisplayFunction
                title={'Insured Party'}
                value={
                  <div className={'d-flex flex-column'}>
                    <span>
                      {info.insuredParty?.firstName} {info.insuredParty?.middleName}
                      {info.insuredParty?.lastName}
                    </span>
                    <span>{info?.insuredParty?.address?.addressLine1 || '-'}</span>
                    <span>{info?.insuredParty?.address?.addressLine2 || ''}</span>
                    <span>
                      {info?.insuredParty?.address?.city || ''}{' '}
                      {info?.insuredParty?.address?.state || ''}{' '}
                      {info?.insuredParty?.address?.postalCode || ''}
                    </span>
                  </div>
                }
              />
            </CCol>
            <CCol>
              <DisplayFunction
                title={'Plan Effective Date'}
                value={
                  info.issuer?.planEffectiveDate
                    ? dateUSFormat(info.issuer?.planEffectiveDate)
                    : '-'
                }
              />
            </CCol>
          </CRow>
          <CRow>
            <CCol>
              <DisplayFunction
                title={'Plan Expiration Date'}
                value={
                  info.issuer?.planExpirationDate
                    ? dateUSFormat(info.issuer?.planExpirationDate)
                    : '-'
                }
              />
            </CCol>
            <CCol>
              <DisplayFunction
                title={'Plan Number'}
                value={
                  info.issuer?.planNumber || insuranceInfoData?.medicareBeneficiaryIdentifier || '-'
                }
              />
            </CCol>
          </CRow>
          <CRow>
            <CCol>
              <DisplayFunction title={'Group Number'} value={info.issuer?.group || '-'} />
            </CCol>
            <CCol>
              <DisplayFunction title={'Group Name'} value={info.issuer?.groupName || '-'} />
            </CCol>
          </CRow>
          <CRow>
            <CCol>
              <DisplayFunction
                title={'Relationship'}
                value={info.insuredParty?.relationship || '-'}
              />
            </CCol>
            {info.payerRank === 'Primary' && (
              <CCol>
                <DisplayFunction title={'Gender'} value={info.insuredParty?.gender || '-'} />
              </CCol>
            )}
          </CRow>
          {info.payerRank === 'Primary' && (
            <CRow>
              <CCol>
                <DisplayFunction
                  title={'DOB'}
                  value={
                    info.insuredParty?.birthDate ? dateUSFormat(info.insuredParty.birthDate) : '-'
                  }
                />
              </CCol>
              <CCol>
                <DisplayFunction
                  title={'Social Beneficiary #'}
                  value={
                    info.insuredParty?.socialBeneficiaryIdentifier ||
                    insuranceInfoData?.socialBeneficiaryIdentifier ||
                    '-'
                  }
                />
              </CCol>
            </CRow>
          )}
          <hr />
        </div>
      ))}
    </>
  )
}

export function PatientClinicalDisplay({ clinicalInfoData, DisplayFunction }) {
  return (
    <>
      <h5 className="mt-3">Vitals</h5>
      {clinicalInfoData?.observation?.map((info, index) => (
        <CRow key={index}>
          <CCol>
            <DisplayFunction
              title={info[0]?.type.split(/(?=[A-Z])/).join(' ')}
              value={
                <>
                  {info[0]?.type !== 'bloodPressure'
                    ? info[0]?.value
                    : info[0].systolicValue + '/' + info[0].diastolicValue}
                  <span className="ms-2">{info[0].unit}</span>
                </>
              }
            />
          </CCol>
          <CCol>
            <DisplayFunction
              title={info[1]?.type.split(/(?=[A-Z])/).join(' ')}
              value={
                <>
                  {info[1]?.value}
                  <span className="ms-2">{info[1]?.unit}</span>
                </>
              }
            />
          </CCol>
        </CRow>
      ))}

      <h5 className="mt-3">Medical Diagnosis</h5>
      {clinicalInfoData?.medDiagnosis?.map((info, index) => (
        <CRow key={index} className="justify-content-start">
          <small className="medDiagnosis-subtitle">
            {info?.clinicalStatus} &middot; {info?.rankDescription}
          </small>
          <p className="medDiagnosis-description">
            {info?.icd10} <small>{info?.icd10Description}</small>
          </p>
        </CRow>
      ))}
      <h5 className="mt-3">Allergies</h5>
      {clinicalInfoData?.allergies?.map((info, index) => (
        <CRow key={index}>
          <CCol>
            <DisplayFunction
              title={
                <>
                  {info[0]?.type.split(/(?=[A-Z])/).join(' ')} &middot; {info[0]?.category} &middot;{' '}
                  {info[0]?.clinicalStatus}
                </>
              }
              value={
                <>
                  {info[0]?.allergen}
                  <span className="ms-2">{info[0].unit}</span>
                </>
              }
            />
            <p className="p-card-subtitle">
              {info[0]?.severity} {info[0]?.reactionType}
            </p>
          </CCol>
          <CCol>
            <DisplayFunction
              title={
                <>
                  {info[1]?.type.split(/(?=[A-Z])/).join(' ')} &middot; {info[1]?.category} &middot;{' '}
                  {info[1]?.clinicalStatus}
                </>
              }
              value={
                <>
                  {info[1]?.allergen}
                  <span>{info[1]?.unit}</span>
                </>
              }
            />
            <p className="p-card-subtitle">
              {info[1]?.severity} {info[1]?.reactionType}
            </p>
          </CCol>
        </CRow>
      ))}

      <h5 className="mt-3">Immunizations</h5>
      {clinicalInfoData?.immunizations?.map((info, index) => (
        <CRow key={index}>
          <CCol>
            <DisplayFunction
              title={
                <>
                  {info[0]?.given} {info[0]?.consentStatus}
                </>
              }
              value={info[0]?.immunization}
            />
          </CCol>
          <CCol>
            <DisplayFunction
              title={
                <>
                  {info[1]?.given} {info[1]?.consentStatus}
                </>
              }
              value={info[1]?.immunization}
            />
          </CCol>
        </CRow>
      ))}
    </>
  )
}

export function PatientLabRadiologyDisplay({ labsAndRadioInfoData, patientId }) {
  return (
    <>
      <h5>Radiology</h5>
      {labsAndRadioInfoData?.radiology?.length === 0 && (
        <p className="labs-and-radio-description">No results found...</p>
      )}
      {labsAndRadioInfoData?.radiology?.map((info, index) => (
        <CRow key={index}>
          <CCol>
            <p className="labs-and-radio-subtitle">
              {info?.reportName || '-'} &middot; {info?.reportStatus || '-'} &middot;
              {info?.issuedDateTime ? twelveHourTimeWithZone(info?.issuedDateTime) : '-'}
            </p>
            {info.reportFile && (
              <CButton
                color="secondary"
                size="sm"
                className="ms-2"
                onClick={() => onPCCDownload(patientId, info)}
              >
                <button>
                  <i
                    style={{ fontSize: '1.2em', marginRight: '4px' }}
                    className="fa-light fa-cloud-arrow-down"
                  ></i>
                  Download
                </button>
              </CButton>
            )}
          </CCol>
        </CRow>
      ))}
      <h5>Laboratory</h5>
      {labsAndRadioInfoData?.laboratory?.length === 0 && (
        <p className="labs-and-radio-description">No results found...</p>
      )}
      {labsAndRadioInfoData?.laboratory?.map((info, index) => (
        <CRow key={index}>
          <CCol>
            <p className="labs-and-radio-subtitle">
              {info?.reportName || '-'} &middot; {info?.reportStatus || '-'} &middot;
              {info?.issuedDateTime ? twelveHourTimeWithZone(info?.issuedDateTime) : '-'}
            </p>
            {info.reportFile && (
              <CButton
                color="secondary"
                size="sm"
                className="ms-2"
                onClick={() => onPCCDownload(patientId, info)}
              >
                <button>
                  <i
                    style={{ fontSize: '1.2em', marginRight: '4px' }}
                    className="fa-light fa-cloud-arrow-down"
                  ></i>
                  Download
                </button>
              </CButton>
            )}
          </CCol>
        </CRow>
      ))}
    </>
  )
}

export function onPCCDownload(patientId, info) {
  getPCCResultFile(patientId, info?.reportId).then((res) => {
    if (res) {
      const link = document.createElement('a')
      // fusion always should return a PDF
      link.href = window.URL.createObjectURL(new Blob([res], { type: 'application/pdf' }))
      const fileName = info?.reportName
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
    }
  })
}

export function CarryOverIcon({ visit, currentPatientAdmitDate }) {
  const isCarryover =
    visit?.admitPatientEvent?.charge_id &&
    visit.admitPatientEvent.charge_id !== visit._id &&
    !currentPatientAdmitDate

  return (
    isCarryover && (
      <i
        title={
          "When Admission Date is not received from an integration or manually entered, Claimocity's analytics predicts the date based on available data"
        }
        className="fa-regular fa-circle-info"
        style={{ color: 'var(--default-text-color)', marginTop: '2px' }}
      ></i>
    )
  )
}

export function DischargeIcon({ visit }) {
  const shouldShow = visit?.dischargePatientEvent?.source === 'COL-API'

  return (
    shouldShow && (
      <i
        title={
          "When Discharge Date is not received from an integration or manually entered, Claimocity's analytics predicts the date based on available data"
        }
        className="fa-regular fa-circle-info"
        style={{ color: 'var(--default-text-color)', marginTop: '2px', pointerEvents: 'auto' }}
      ></i>
    )
  )
}

// dont send an entire patient to the backend
// only send the fields needed
// uses `router.post("/visits"` as a reference
export function trimPatientObject(patient = {}) {
  let {
    amendments,
    dob,
    ethnicity,
    gender,
    genderIdentity,
    patient_lastname,
    patient_firstname,
    patient_middlename,
    preferredLanguage,
    primary_care_physician,
    race,
    sexualOrientation,
  } = { ...patient }
  return {
    amendments,
    dob,
    ethnicity,
    gender,
    genderIdentity,
    patient_lastname,
    patient_firstname,
    patient_middlename,
    preferredLanguage,
    primary_care_physician,
    race,
    sexualOrientation,
  }
}

export async function getPCPObject(pcp_id) {
  if (!pcp_id) {
    return
  }

  try {
    return await getFusion(`v1/patient/pcp/${pcp_id}`)
  } catch (e) {
    console.error(e)
    return ''
  }
}
